/**
 * EntryStatusBadge - shared presentational graded entry-status pill.
 *
 * Single rendering primitive for the graded buy-readiness badge
 * (strong_buy / buy / accumulate / wait_for_reversal / holding / extended /
 * below_stop / unknown) across EVERY chart surface (ChartModal, ChartFocusTab,
 * AnalysisPanel, the deep-chart LevelsSummaryOverlay, the Charts-tab grid card).
 *
 * Source of truth: `lib/deepAnalysis/entryStatusDisplay.ts` - label / emoji /
 * Tailwind tones come from `resolveEntryStatus`; the i18n key from
 * `ENTRY_STATUS_I18N_KEY`. i18n keys are global under `resultsTableVirtualized.*`
 * (mirrors ResultsTableVirtualized.renderEntryStatus + mobile/StockCard).
 *
 * Renders NOTHING for an unknown/missing code (`isKnownEntryStatus === false`) -
 * the table's "-" placeholder is table-local and intentionally not reproduced
 * here. Uses semantic design-system Tailwind tokens (no raw hex, no `tv-*`).
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  resolveEntryStatus,
  isKnownEntryStatus,
  resolveLiveDivergence,
  buildLiveDivergenceTooltip,
  ENTRY_STATUS_I18N_KEY,
} from '../../lib/deepAnalysis/entryStatusDisplay';
import type { StockData } from '../../types';
import { cn } from '../../lib/utils';

export interface EntryStatusBadgeProps {
  /** StockData row - preferred source for the code + instruction. */
  stock?: StockData | undefined;
  /** Explicit code override / source when no `stock` row is available. */
  code?: string | null | undefined;
  /** Explicit LIVE coarse code (charts grid passes `code`+`liveCode`, not a
   *  `stock` row). `stock.live_entry_status` wins when a row is supplied. */
  liveCode?: string | null | undefined;
  /** Explicit instruction override / source for the hover tooltip. */
  instruction?: string | null | undefined;
  /** Smaller variant for dense card headers (Charts-tab grid). */
  compact?: boolean;
  /** Extra Tailwind classes appended to the pill. */
  className?: string;
}

/**
 * Resolution precedence: `stock.entry_status` wins over the explicit `code`
 * prop; `stock.entry_instruction` wins over the explicit `instruction` prop.
 * Either path may be used standalone (StockData row OR raw strings).
 */
const EntryStatusBadge: React.FC<EntryStatusBadgeProps> = ({
  stock,
  code,
  liveCode,
  instruction,
  compact = false,
  className,
}) => {
  const { t } = useTranslation();

  const resolvedCode = stock?.entry_status ?? code ?? undefined;
  if (!isKnownEntryStatus(resolvedCode)) return null;

  const display = resolveEntryStatus(resolvedCode);
  const label = t(`resultsTableVirtualized.${ENTRY_STATUS_I18N_KEY[display.code]}`, {
    defaultValue: display.label,
  });

  // Live-divergence hint (PLAN_ENTRY_STATUS_PARITY_2026-06-22) - shared decision
  // via resolveLiveDivergence so the table + this badge can't drift. Surfaces a
  // muted live emoji + tooltip suffix when the screened grade was kept through an
  // intraday drift (screened Buy, live Holding). No hint when they agree.
  const { show: showLiveHint, liveDisplay } = resolveLiveDivergence(
    resolvedCode,
    stock?.live_entry_status ?? liveCode,
  );
  const liveLabel = liveDisplay
    ? t(`resultsTableVirtualized.${ENTRY_STATUS_I18N_KEY[liveDisplay.code]}`, {
        defaultValue: liveDisplay.label,
      })
    : '';

  // Prefer the backend's plain-language entry_instruction as the tooltip
  // (SPEC_ENTRY_CONVICTION_2026-06-19); fall back to the badge label.
  const rawInstruction = stock?.entry_instruction ?? instruction;
  const baseTooltip =
    typeof rawInstruction === 'string' && rawInstruction.trim()
      ? rawInstruction
      : label;
  const tooltip = showLiveHint
    ? buildLiveDivergenceTooltip(
        baseTooltip,
        t('resultsTableVirtualized.liveStatusPrefix', { defaultValue: 'Live' }),
        liveLabel,
      )
    : baseTooltip;

  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full font-semibold',
        compact ? 'px-1.5 py-0 text-[9px]' : 'px-2 py-0.5 text-xs',
        display.badgeClass,
        className
      )}
      title={tooltip}
    >
      {display.emoji && <span aria-hidden>{display.emoji}</span>}
      <span>{label}</span>
      {showLiveHint && liveDisplay && (
        <span aria-hidden className="opacity-60">{liveDisplay.emoji || '•'}</span>
      )}
    </span>
  );
};

export default EntryStatusBadge;
